import { defined } from '@vy/null';
import type { CustomerMasterCustomer, CustomerMasterReport, IntegrationProviders } from '@vy/types';
import { CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES } from '../customer-master-report-page-components/customer-master-report-page-table/customer-master-report-constants';
import type { CustomerMasterRow } from '../customer-master-report-page-components/customer-master-report-page-table/customer-master-row-types';
import { isColumnVisibleForConnectedIntegrations } from '../customer-master-report-page-components/customer-master-report-page-table/integration-column-visibility';
import { formatDueDate } from '../../../../utils/format-due-date';
import { buildFlatCustomerMasterRows } from './customer-master-row-builder';

export type CustomerMasterExportRow = Record<string, string | number | boolean | Date | null>;

export function flattenCustomerMasterForExport(
  report: CustomerMasterReport,
  connectedProviders: ReadonlySet<IntegrationProviders>,
): CustomerMasterExportRow[] {
  const flatRows = buildFlatCustomerMasterRows(report);
  const maxContactsCount = report.reduce(
    (currentMax, customer) => Math.max(currentMax, customer.contacts?.length ?? 0),
    0,
  );

  return flatRows.map((row) => buildExportRow(row, maxContactsCount, connectedProviders));
}

function buildExportRow(
  row: CustomerMasterRow,
  maxContactsCount: number,
  connectedProviders: ReadonlySet<IntegrationProviders>,
): CustomerMasterExportRow {
  const customerColumns: CustomerMasterExportRow = {
    customerStatus: row.customerStatus ?? null,
    customerName: row.customerName,
    customerId: row.customerId ?? null,
    legalName: row.legalName ?? null,
    customerAlias: row.customerAlias ?? null,
    customerType: row.customerType ?? null,
    dueDate: formatDueDate(row.dueDate),
    parentCustomer: row.parentCustomer ?? null,
    crmId: row.crmId ?? null,
    nsErpId: row.nsErpId ?? null,
    qbErpId: row.qbErpId ?? null,
    xeroErpId: row.xeroErpId ?? null,
    stripeId: row.stripeId ?? null,
    subsidiary: row.subsidiary ?? null,
    taxId: row.taxId ?? null,
    customerCurrency: row.customerCurrency ?? null,
    taxMethod: row.taxMethod ?? null,
    taxRate: row.taxRate ?? null,
    manualReview: row.manualReview ?? null,
    paymentProvider: row.paymentProvider ?? null,
    autoPay: row.autoPay ?? null,
    customerNotes: row.customerNotes ?? null,
    wallet: row.wallet ?? null,
    createdAt: row.createdAt,
    country: row.address?.country ?? null,
    state: row.address?.state ?? null,
    city: row.address?.city ?? null,
    addressText: row.address?.addressText ?? null,
    zipCode: row.address?.postalCode ?? null,
  };

  const contractColumns: CustomerMasterExportRow = {
    contractId: row.contractId ?? null,
    contractName: row.contractName ?? null,
    contractStatus: row.contractStatus ?? null,
    startDate: row.startDate ?? null,
    endDate: row.endDate ?? null,
    daysToEnd: row.daysToEnd ?? null,
    durationMonths: row.durationMonths ?? null,
    currency: row.currency ?? null,
    estimatedAmount: row.estimatedAmount ?? null,
    mrr: row.mrr ?? null,
    autoRenewal: row.autoRenewal ?? null,
    trialPeriod: row.trialPeriod ?? null,
    purchaseOrder: row.purchaseOrder ?? null,
    accountManager: row.accountManager ?? null,
    salesforceOpportunityId: row.salesforceOpportunityId ?? null,
    netSuiteSalesOrderId: row.netSuiteSalesOrderId ?? null,
    syncStatusUpdatedAt: row.syncStatusUpdatedAt ?? null,
  };

  const customerCustomFieldColumns: CustomerMasterExportRow = {};
  for (const customField of row.customFieldValues ?? []) {
    if (defined(customField.fieldName)) {
      customerCustomFieldColumns[
        `${CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES.customer}${customField.fieldName}`
      ] = normalizeCustomFieldValue(customField.value);
    }
  }

  const contractCustomFieldColumns: CustomerMasterExportRow = {};
  for (const customField of row.contractCustomFieldValues ?? []) {
    if (defined(customField.fieldName)) {
      contractCustomFieldColumns[
        `${CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES.contract}${customField.fieldName}`
      ] = normalizeCustomFieldValue(customField.value);
    }
  }

  const contactColumns = buildContactColumns(row.contacts ?? [], maxContactsCount);

  const mergedRow: CustomerMasterExportRow = {
    ...customerColumns,
    ...contractColumns,
    ...customerCustomFieldColumns,
    ...contractCustomFieldColumns,
    ...contactColumns,
  };

  const filteredMergedRowEntries = Object.entries(mergedRow).filter(([key]) =>
    isColumnVisibleForConnectedIntegrations(key, connectedProviders),
  );

  return Object.fromEntries(filteredMergedRowEntries);
}

function buildContactColumns(
  contacts: NonNullable<CustomerMasterCustomer['contacts']>,
  maxContactsCount: number,
): CustomerMasterExportRow {
  const contactColumns: CustomerMasterExportRow = {};
  for (let contactIndex = 0; contactIndex < maxContactsCount; contactIndex++) {
    const contactNumber = contactIndex + 1;
    const contact = contacts[contactIndex];
    contactColumns[`contact${contactNumber}Name`] = contact?.name ?? null;
    contactColumns[`contact${contactNumber}Title`] = contact?.title ?? null;
    contactColumns[`contact${contactNumber}Phone`] = contact?.phone ?? null;
    contactColumns[`contact${contactNumber}Email`] = contact?.email ?? null;
  }
  return contactColumns;
}

function normalizeCustomFieldValue(
  value: string | number | boolean | null | undefined,
): string | number | boolean | null {
  return value ?? null;
}
